import React from 'react';
import { 
  UserIcon,
  ShieldCheckIcon,
  CreditCardIcon,
  BellIcon,
  CodeBracketSquareIcon,
  SquaresPlusIcon,
  ArrowRightOnRectangleIcon,
  PencilIcon,
  EnvelopeIcon,
  ChevronRightIcon,
  QuestionMarkCircleIcon,
  ChatBubbleLeftEllipsisIcon
} from '@heroicons/react/24/outline';
import { RocketLaunchIcon, CheckBadgeIcon, CubeIcon } from '@heroicons/react/24/solid';

export default function UserProfile() {
  return (
    <div className="flex h-screen bg-slate-50 font-sans overflow-hidden">
      
      {/* Sidebar (Settings Specific) */}
      <aside className="w-[280px] bg-white border-r border-slate-200 flex flex-col justify-between hidden md:flex shrink-0">
        <div className="flex flex-col h-full">
          {/* Logo Area */}
          <div className="h-24 flex items-center px-8 shrink-0">
            <div className="flex items-center gap-3.5">
              <div className="w-10 h-10 bg-brand-500 rounded-full flex items-center justify-center shadow-md shadow-brand-500/20 shrink-0">
                <RocketLaunchIcon className="w-5 h-5 text-white" />
              </div>
              <h1 className="text-[20px] font-extrabold text-slate-900 tracking-tight">Founder Finder</h1>
            </div>
          </div>
          
          {/* Navigation Links */}
          <div className="px-6 pt-4">
            <p className="text-[11px] font-bold text-slate-400 uppercase tracking-widest mb-4 pl-2">
              Workspace Control
            </p>
            <nav className="space-y-1.5 flex-1 overflow-y-auto">
              <button className="w-full flex items-center gap-3.5 px-4 py-3 rounded-xl text-[15px] font-semibold bg-brand-50 text-brand-600 transition-colors">
                <UserIcon className="w-[22px] h-[22px] shrink-0" />
                Profile Settings
              </button>
              <button className="w-full flex items-center gap-3.5 px-4 py-3 rounded-xl text-[15px] font-semibold text-slate-500 hover:bg-slate-50 hover:text-slate-900 transition-colors">
                <ShieldCheckIcon className="w-[22px] h-[22px] shrink-0" />
                Security
              </button>
              <button className="w-full flex items-center gap-3.5 px-4 py-3 rounded-xl text-[15px] font-semibold text-slate-500 hover:bg-slate-50 hover:text-slate-900 transition-colors">
                <CreditCardIcon className="w-[22px] h-[22px] shrink-0" />
                Billing & Plans
              </button>
              <button className="w-full flex items-center gap-3.5 px-4 py-3 rounded-xl text-[15px] font-semibold text-slate-500 hover:bg-slate-50 hover:text-slate-900 transition-colors">
                <BellIcon className="w-[22px] h-[22px] shrink-0" />
                Notifications
              </button>
              <button className="w-full flex items-center gap-3.5 px-4 py-3 rounded-xl text-[15px] font-semibold text-slate-500 hover:bg-slate-50 hover:text-slate-900 transition-colors">
                <CodeBracketSquareIcon className="w-[22px] h-[22px] shrink-0" />
                API Ecosystem
              </button>
              <button className="w-full flex items-center gap-3.5 px-4 py-3 rounded-xl text-[15px] font-semibold text-slate-500 hover:bg-slate-50 hover:text-slate-900 transition-colors">
                <SquaresPlusIcon className="w-[22px] h-[22px] shrink-0" />
                Integrations
              </button>
            </nav>
          </div>
        </div>

        {/* Bottom Section */}
        <div className="p-6 flex flex-col gap-4 shrink-0">
          {/* Upgrade Card */}
          <div className="bg-[#0f172a] rounded-2xl p-5 shadow-xl shadow-slate-900/10">
            <h3 className="text-white text-[12px] font-bold uppercase tracking-widest mb-2 opacity-80">Elite V2 Pro</h3>
            <p className="text-slate-300 text-[13px] leading-relaxed mb-4">
              40% faster inference & early model access.
            </p>
            <button className="w-full bg-white text-slate-900 py-2.5 rounded-xl text-sm font-bold hover:bg-slate-100 hover:scale-[1.02] active:scale-95 transition-all">
              Upgrade Now
            </button>
          </div>

          <button className="w-full flex items-center gap-3.5 px-3 py-2 text-[15px] font-bold text-red-500 hover:bg-red-50 rounded-xl transition-colors">
            <ArrowRightOnRectangleIcon className="w-6 h-6 shrink-0" /> Sign Out
          </button>
        </div>
      </aside>

      {/* Main Content Area */}
      <div className="flex-1 flex flex-col h-screen overflow-hidden">
        
        {/* Top Header */}
        <header className="h-[88px] bg-white border-b border-slate-200 flex items-center justify-between px-10 shrink-0 relative z-20">
          
          {/* Center Navigation Links */}
          <nav className="flex items-center gap-8 h-full pt-1">
            <button className="text-[15px] font-semibold text-slate-500 hover:text-slate-900 transition-colors h-full flex items-center">Dashboard</button>
            <button className="text-[15px] font-semibold text-slate-500 hover:text-slate-900 transition-colors h-full flex items-center">Analytics</button>
            <button className="text-[15px] font-semibold text-slate-500 hover:text-slate-900 transition-colors h-full flex items-center">Team</button>
            <button className="text-[15px] font-bold text-brand-600 h-full flex items-center border-b-2 border-brand-500 pt-[2px]">Settings</button>
          </nav>
          
          {/* Right Header Actions */}
          <div className="flex items-center gap-6">
            <button className="text-slate-400 hover:text-slate-900 transition-colors">
              <BellIcon className="w-6 h-6" />
            </button>
            {/* Profile Avatar */}
            <div className="w-10 h-10 rounded-full bg-slate-200 flex items-center justify-center border border-slate-300 overflow-hidden cursor-pointer hover:shadow-md transition-shadow">
              <img src="https://i.pravatar.cc/150?img=11" alt="Profile" className="w-full h-full object-cover" />
            </div>
          </div>
        </header>

        {/* Scrollable Page Content */}
        <main className="flex-1 overflow-y-auto bg-slate-50 px-10 py-10">
          <div className="max-w-[1200px] mx-auto">
            
            {/* Page Header */}
            <div className="flex flex-col md:flex-row md:items-start justify-between gap-6 mb-8">
              <div>
                <div className="flex items-center gap-2 text-[11px] font-bold text-slate-400 uppercase tracking-widest mb-3">
                  <span>Workspace</span>
                  <ChevronRightIcon className="w-3 h-3" />
                  <span className="text-slate-900">Settings</span>
                </div>
                <h1 className="text-[36px] font-extrabold text-slate-900 tracking-tight">Account Profile</h1>
                <p className="text-[16px] text-slate-500 mt-2 font-medium max-w-lg leading-relaxed">
                  Customize your presence and configure security protocols for the platform.
                </p>
              </div>
              <div className="flex items-center gap-4 pt-4">
                <button className="px-6 py-3 text-[14px] font-bold text-slate-500 hover:text-slate-900 transition-colors">
                  Discard
                </button>
                <button className="bg-brand-500 text-white px-6 py-3 rounded-xl text-[14px] font-bold hover:bg-brand-600 transition-colors shadow-lg shadow-brand-500/25">
                  Save Changes
                </button>
              </div>
            </div>

            {/* Content Grid */}
            <div className="grid grid-cols-1 xl:grid-cols-3 gap-8">
              
              {/* Left Column (Forms) */}
              <div className="xl:col-span-2 flex flex-col gap-8">
                
                {/* Identity Details Card */}
                <div className="bg-white border border-slate-200 rounded-[24px] p-10 shadow-sm">
                  <div className="flex flex-col md:flex-row gap-8 mb-10">
                    <div className="relative shrink-0">
                      <div className="w-28 h-28 rounded-full border-4 border-white shadow-lg overflow-hidden bg-slate-100">
                        <img src="https://i.pravatar.cc/150?img=11" alt="Alexander Sterling" className="w-full h-full object-cover" />
                      </div>
                      <button className="absolute bottom-0 right-0 w-8 h-8 bg-brand-500 text-white rounded-full flex items-center justify-center border-2 border-white shadow-sm hover:bg-brand-600 transition-colors">
                        <PencilIcon className="w-4 h-4" />
                      </button>
                    </div>
                    <div>
                      <div className="flex items-center gap-3 mb-2">
                        <h2 className="text-[24px] font-extrabold text-slate-900">Identity<br/>Details</h2>
                        <div className="flex gap-2 self-start mt-2">
                          <span className="px-2.5 py-1 bg-blue-50 text-brand-600 text-[10px] font-extrabold uppercase tracking-widest rounded-md border border-brand-100/50">Verified</span>
                          <span className="px-2.5 py-1 bg-emerald-50 text-emerald-600 text-[10px] font-extrabold uppercase tracking-widest rounded-md border border-emerald-100/50">Online</span>
                        </div>
                      </div>
                      <p className="text-[15px] text-slate-500 font-medium leading-relaxed max-w-md">
                        Your public metadata is visible to team members within the workspace. Ensure your designation is current.
                      </p>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                    <div>
                      <label className="block text-[11px] font-bold text-slate-400 uppercase tracking-widest mb-2.5 ml-1">Legal Full Name</label>
                      <input 
                        type="text" 
                        defaultValue="Alexander Sterling"
                        className="w-full bg-slate-50 border border-slate-200 text-slate-900 text-[15px] font-bold px-5 py-3.5 rounded-2xl focus:outline-none focus:ring-2 focus:ring-brand-500/20 transition-all"
                      />
                    </div>
                    <div>
                      <label className="block text-[11px] font-bold text-slate-400 uppercase tracking-widest mb-2.5 ml-1">Professional Designation</label>
                      <input 
                        type="text" 
                        defaultValue="Principal AI Architect"
                        className="w-full bg-slate-50 border border-slate-200 text-slate-900 text-[15px] font-bold px-5 py-3.5 rounded-2xl focus:outline-none focus:ring-2 focus:ring-brand-500/20 transition-all"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-[11px] font-bold text-slate-400 uppercase tracking-widest mb-2.5 ml-1">Primary Email Protocol</label>
                    <div className="relative">
                      <EnvelopeIcon className="w-5 h-5 text-slate-400 absolute left-5 top-1/2 -translate-y-1/2" />
                      <input 
                        type="email" 
                        defaultValue="alex.sterling@elite-enterprise.ai"
                        className="w-full bg-slate-50 border border-slate-200 text-slate-900 text-[15px] font-bold pl-12 pr-5 py-3.5 rounded-2xl focus:outline-none focus:ring-2 focus:ring-brand-500/20 transition-all"
                      />
                    </div>
                  </div>
                </div>

                {/* Bottom Row inside left column */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                  {/* Security Protocol */}
                  <div className="bg-white border border-slate-200 rounded-[24px] p-8 shadow-sm flex flex-col">
                    <div className="flex items-center gap-4 mb-8">
                      <div className="w-12 h-12 bg-[#0f172a] rounded-xl flex items-center justify-center shrink-0">
                        <ShieldCheckIcon className="w-6 h-6 text-white" />
                      </div>
                      <h3 className="text-[13px] font-extrabold text-slate-900 uppercase tracking-widest">Security Protocol</h3>
                    </div>

                    <div className="flex items-center justify-between p-4 hover:bg-slate-50 rounded-xl cursor-pointer transition-colors mb-2 border border-transparent hover:border-slate-100">
                      <div>
                        <h4 className="text-[14px] font-bold text-slate-900 mb-0.5">Access Password</h4>
                        <p className="text-[12px] text-slate-400 font-medium">Updated 84 days ago</p>
                      </div>
                      <ChevronRightIcon className="w-5 h-5 text-slate-300" />
                    </div>

                    <div className="bg-slate-50 border border-slate-200 rounded-2xl p-5 flex items-center justify-between mt-auto">
                      <div className="flex items-start gap-3">
                        <div className="mt-1 w-2.5 h-2.5 rounded-full bg-emerald-500 shrink-0"></div>
                        <div>
                          <h4 className="text-[14px] font-bold text-slate-900 mb-0.5">Multi-Factor Auth</h4>
                          <p className="text-[12px] text-slate-500 font-medium leading-tight max-w-[120px]">YubiKey & Authenticator</p>
                        </div>
                      </div>
                      <span className="px-2.5 py-1 bg-blue-100/50 text-brand-600 text-[10px] font-extrabold uppercase tracking-widest rounded-md">Active</span>
                    </div>
                  </div>

                  {/* Communication */}
                  <div className="bg-white border border-slate-200 rounded-[24px] p-8 shadow-sm flex flex-col">
                    <div className="flex items-center gap-4 mb-8">
                      <div className="w-12 h-12 bg-[#0f172a] rounded-xl flex items-center justify-center shrink-0">
                        <BellIcon className="w-6 h-6 text-white" />
                      </div>
                      <h3 className="text-[13px] font-extrabold text-slate-900 uppercase tracking-widest">Communication</h3>
                    </div>

                    <div className="flex flex-col gap-2">
                      <div className="p-4 hover:bg-slate-50 rounded-xl cursor-pointer transition-colors border border-transparent hover:border-slate-100">
                        <h4 className="text-[14px] font-bold text-slate-900 mb-0.5">Email Intelligence</h4>
                        <p className="text-[12px] text-slate-400 font-medium">Weekly insight summaries</p>
                      </div>
                      
                      <div className="p-4 hover:bg-slate-50 rounded-xl cursor-pointer transition-colors border border-transparent hover:border-slate-100">
                        <h4 className="text-[14px] font-bold text-slate-900 mb-0.5">Direct Alerts</h4>
                        <p className="text-[12px] text-slate-400 font-medium">Real-time system updates</p>
                      </div>
                    </div>
                  </div>
                </div>

              </div>

              {/* Right Column */}
              <div className="xl:col-span-1 flex flex-col gap-8">
                
                {/* Enterprise Tier Card */}
                <div className="bg-gradient-to-b from-[#113a7c] to-brand-700 rounded-[32px] p-10 text-white shadow-xl shadow-brand-900/20 relative overflow-hidden">
                  {/* Decorative blur */}
                  <div className="absolute top-0 right-0 -mr-10 -mt-10 w-40 h-40 bg-white/10 blur-3xl rounded-full"></div>

                  <div className="flex items-start justify-between mb-10 relative z-10">
                    <div className="w-12 h-12 rounded-2xl bg-white/10 border border-white/20 flex items-center justify-center backdrop-blur-md">
                      <CubeIcon className="w-6 h-6 text-white" />
                    </div>
                    <span className="px-3 py-1.5 bg-white/10 border border-white/20 rounded-full text-[10px] font-extrabold tracking-widest uppercase backdrop-blur-md">
                      Active Tier
                    </span>
                  </div>

                  <div className="relative z-10 mb-8">
                    <div className="flex items-center gap-2 mb-2">
                      <h2 className="text-[32px] font-extrabold tracking-tight">Enterprise</h2>
                      <CheckBadgeIcon className="w-6 h-6 text-emerald-400" />
                    </div>
                    <p className="text-brand-100 text-[13px] font-medium opacity-80">
                      Standard License • Since 2023
                    </p>
                  </div>

                  <div className="bg-[#002f87]/50 border border-white/10 rounded-2xl p-6 mb-8 relative z-10 backdrop-blur-sm">
                    <p className="text-[10px] font-extrabold text-brand-200 uppercase tracking-widest mb-1">Credits Left</p>
                    <div className="text-[36px] font-extrabold tracking-tight mb-4">2,480</div>
                    <div className="w-full h-2 bg-white/10 rounded-full overflow-hidden">
                      <div className="w-[45%] h-full bg-white rounded-full"></div>
                    </div>
                  </div>

                  <div className="flex flex-col gap-3 relative z-10">
                    <button className="w-full bg-white text-brand-900 py-3.5 rounded-xl text-[14px] font-bold hover:bg-slate-50 transition-colors shadow-sm">
                      Purchase Credits
                    </button>
                    <button className="w-full bg-white/10 border border-white/20 text-white py-3.5 rounded-xl text-[14px] font-bold hover:bg-white/20 transition-colors backdrop-blur-sm">
                      Manage Subscription
                    </button>
                  </div>
                </div>

                {/* Need Assistance Card */}
                <div className="bg-white border border-slate-200 rounded-[32px] p-8 shadow-sm">
                  <h3 className="text-[11px] font-bold text-slate-400 uppercase tracking-widest mb-6 ml-2">Need Assistance?</h3>
                  
                  <div className="flex flex-col gap-6">
                    <div className="flex items-start gap-4 cursor-pointer group">
                      <div className="w-10 h-10 rounded-full bg-slate-50 border border-slate-100 flex items-center justify-center shrink-0 group-hover:bg-brand-50 transition-colors">
                        <QuestionMarkCircleIcon className="w-5 h-5 text-slate-400 group-hover:text-brand-500 transition-colors" />
                      </div>
                      <div>
                        <h4 className="text-[14px] font-bold text-slate-900 mb-0.5 group-hover:text-brand-600 transition-colors">Enterprise Help Center</h4>
                        <p className="text-[12px] text-slate-400 font-medium">Documentation & Guides</p>
                      </div>
                    </div>

                    <div className="flex items-start gap-4 cursor-pointer group">
                      <div className="w-10 h-10 rounded-full bg-slate-50 border border-slate-100 flex items-center justify-center shrink-0 group-hover:bg-brand-50 transition-colors">
                        <ChatBubbleLeftEllipsisIcon className="w-5 h-5 text-slate-400 group-hover:text-brand-500 transition-colors" />
                      </div>
                      <div>
                        <h4 className="text-[14px] font-bold text-slate-900 mb-0.5 group-hover:text-brand-600 transition-colors">Priority Support</h4>
                        <p className="text-[12px] text-slate-400 font-medium leading-tight">24/7 Human Intelligence</p>
                      </div>
                    </div>
                  </div>
                </div>

              </div>

            </div>
          </div>
        </main>

      </div>
    </div>
  );
}